<script lang="ts" setup>
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { computed } from 'vue'

const {
  description,
  xDivider = false,
  disabled = false,
  rightIcon,
  sizePreset = AccountRowSizePreset.H56px,
  avatarClasses,
  descriptionClasses,
  color = OzBoxColors.PrimaryLight,
  dataTestid,
} = defineProps<{
  avatarUrl
  title: string
  description?: string
  xDivider?: boolean
  disabled?: boolean
  rightIcon?: string
  sizePreset?: AccountRowSizePreset
  buttonClasses?: string
  avatarClasses?: string
  descriptionClasses?: string
  color?: OzBoxColors.PrimaryLight | OzBoxColors.PrimaryBlur
  dataTestid?: string
}>()

const avatarSize = computed<number>(() => {
  if (sizePreset === AccountRowSizePreset.H48px) return 32
  return 40
})

const emit = defineEmits<{
  (name: 'click'): void
}>()
</script>

<script lang="ts">
export enum AccountRowSizePreset {
  H48px = 'H48px',
  H56px = 'H56px',
}
</script>

<template>
  <div class="flex flex-col">
    <OzBaseButton
      :class="[
        sizePreset === AccountRowSizePreset.H48px && 'py-1.5 ps-2 pe-3',
        sizePreset === AccountRowSizePreset.H56px && 'py-2 px-2',
        'bg-transparent group flex items-center my-1 rounded-xl',
        !disabled && [
          // Hover
          color === OzBoxColors.PrimaryLight && 'hhover:bg-button-light dark:hhover:bg-button-dark',
          color === OzBoxColors.PrimaryBlur && 'hhover:bg-light-ui-100 dark:hhover:bg-dark-ui-100',
          // Active
          'hactive:bg-button-light hactive:dark:bg-button-dark',
        ],
        // Focus
        'focus-visible:ring-2',
        'focus-visible:ring-grape-500 dark:focus-visible:ring-canary-500',
        'focus-visible:bg-light-ui-100 dark:focus-visible:bg-dark-ui-100',
        buttonClasses,
      ]"
      :disabled="disabled"
      :data-testid="dataTestid"
      @click="emit('click')"
    >
      <OzAvatar
        :height="avatarSize"
        :width="avatarSize"
        :src="avatarUrl"
        :alt-text-name="title"
        :class="avatarClasses || 'rounded-xl'"
        aria-hidden="true"
      />

      <span
        :class="[
          'flex justify-between items-center flex-1',
          'box-border',
          'min-w-0',
          'ps-0.5 pe-1',
          sizePreset === AccountRowSizePreset.H48px && 'ms-4',
          sizePreset === AccountRowSizePreset.H56px && 'ms-3.5',
        ]"
      >
        <span :class="['flex flex-col flex-1', 'min-w-0', 'text-start']">
          <span
            :class="[
              'truncate',
              sizePreset === AccountRowSizePreset.H48px && 'text-body-small',
              sizePreset === AccountRowSizePreset.H56px && 'text-body',
              !disabled
                ? 'text-dark-text-100 dark:text-light-text-100 group-active:text-grape-500 dark:group-active:text-canary-500'
                : 'text-dark-text-200 dark:text-light-text-200',
            ]"
            :title="title"
          >
            {{ title }}
          </span>
          <span
            v-if="description"
            :class="[
              'truncate',
              sizePreset === AccountRowSizePreset.H48px && 'text-12-16',
              sizePreset === AccountRowSizePreset.H56px && 'text-body-extra-small',
              !disabled ? 'text-dark-text-200 dark:text-light-text-200' : 'text-dark-text-300 dark:text-light-text-300',
              descriptionClasses,
            ]"
            :title="description"
          >
            {{ description }}
          </span>
        </span>

        <OzIcon
          v-if="rightIcon"
          :name="rightIcon"
          :size="14"
          class="ms-2 text-dark-text-200 dark:text-light-text-200"
        />
      </span>
    </OzBaseButton>
    <OzDivider
      v-if="xDivider"
      :color-scheme="OzDividerColorScheme.Solid"
      :class="[
        sizePreset === AccountRowSizePreset.H48px && 'ms-13.5',
        sizePreset === AccountRowSizePreset.H56px && 'ms-15.5',
        'me-2',
      ]"
    />
  </div>
</template>
